import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DateTime } from 'luxon';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useHasVerifiedPhone } from './use-phone-verification-status';
import { useIsAccountEmailVerifiedSellerSetting } from './use-seller-setting';
import { useUserHasAdministrationRole } from './use-user-role';
import { PHONE_VERIFICATION_STATES } from '../authentication/constants';
import { useIsAccountLimited } from './account-information/use-show-limit-information';
import { useIsSellerInTrial } from './use-is-seller-in-trial';
import { useTaxIdBanner } from './use-tax-id-banner';
import {
  usePhoneNumberCaptureBannerDismissed,
  useUspsNsaCopOnboardingCompleted,
  useUspsNsaModalDismissedAtUTC,
  useUspsNsaWaitingBannerDismissed,
} from './use-user-setting';
import { useShouldShowApiKeysExpiringBanner } from '../components/header-notification-banner/api-keys-expiring-banner';
import { useShouldShowPlanTrialBanner } from '../components/header-notification-banner/billing-plan-trial-banner/use-should-show-plan-trial-banner';
import { useShouldShowPlanTrialExpiredBanner } from '../components/header-notification-banner/billing-plan-trial-expired-banner/use-should-show-expired-banner';
import { useShippingPlanInfo } from '../components/header-notification-banner/shipping-limit-banner/use-shipping-plan-info';
import { emptyObject } from 'ui-components/default-props/empty';
import { Providers } from '../enums/providers';
import { UserSettings } from '../enums/user-settings/user-settings';
import { useEnablePhoneNumberCapture } from '../feature-flags/hooks/use-enable-phone-number-capture';
import { useIsMerchantUspsCopEligible } from '../feature-flags/hooks/use-is-merchant-usps-cop-eligible';
import { useIsUspsCopFeatureEnabled } from '../feature-flags/hooks/use-is-usps-cop-feature-enabled';
import { PAYMENT_ISSUE, PENDING_CANCELLATION } from '../pages/settings/payment-and-subscription/constants/account-issues';
import { useDeriveSubscriptionInfo } from '../pages/settings/payment-and-subscription/hooks/use-derive-subscription-info';
import { Auth0Wrapper } from '../services/auth0/Auth0Wrapper';
import { getCurrentDateTimeAsISO, getCurrentDateTimeAsUtcIso, getDaysBetweenISODates } from '../services/datetime-service';
import Global from '../store/global-selectors';
import { saveUserSetting } from '../store/user/user.actions';
import { getSellerProviderAccountLink, getUspsOnboardStatus } from '../wallet/utilities/api';

const DAYS_TO_SHOW_WARNING = 14;

export const useShouldShowTrialWarningBanner = () => {
  const isInTrial = useIsSellerInTrial();

  const planInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const trialEndDate = planInfo?.trialEndDate;

  const daysLeftInTrial = getDaysBetweenISODates(getCurrentDateTimeAsISO(), trialEndDate);

  const shouldShowExpiredBanner = daysLeftInTrial <= 0;
  const shouldShowWarningBanner = daysLeftInTrial <= DAYS_TO_SHOW_WARNING && daysLeftInTrial > 0;

  return isInTrial && (shouldShowWarningBanner || shouldShowExpiredBanner);
};

export const useShouldShowTrialInfoBanner = () => {
  const isInTrial = useIsSellerInTrial();
  const planInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const trialEndDate = planInfo?.trialEndDate;

  const daysLeftInTrial = getDaysBetweenISODates(getCurrentDateTimeAsISO(), trialEndDate);

  const shouldShowInfoBanner = daysLeftInTrial > DAYS_TO_SHOW_WARNING;

  return isInTrial && shouldShowInfoBanner;
};

export const useShouldShowPastDueBanner = () => {
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const { accountIssueStatus } = useDeriveSubscriptionInfo(planInfo ?? emptyObject);
  return accountIssueStatus === PAYMENT_ISSUE;
};

export const useGetTrialBannerData = () => {
  const intl = useIntl();
  const planInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const trialEndDate = planInfo?.trialEndDate;

  const daysLeftInTrial = getDaysBetweenISODates(getCurrentDateTimeAsISO(), trialEndDate);
  const formattedDate = intl.formatDate(trialEndDate, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return {
    formattedDate,
    daysLeftInTrial,
  };
};

export const useShouldShowUndoCancellationBanner = () => {
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const { accountIssueStatus } = useDeriveSubscriptionInfo(planInfo);
  return accountIssueStatus === PENDING_CANCELLATION;
};

export const useShouldShowPhoneVerificationBanner = () => {
  const { verifiedPhoneState } = useHasVerifiedPhone();

  if (!Auth0Wrapper.isUsingAuth0Auth()) {
    return false;
  }

  return (
    verifiedPhoneState === PHONE_VERIFICATION_STATES.BANNER ||
    verifiedPhoneState === PHONE_VERIFICATION_STATES.MODAL ||
    verifiedPhoneState === PHONE_VERIFICATION_STATES.UNSKIPPABLE
  );
};

/**
 * Custom hook to manage USPS NSA onboard status with caching
 * Makes API calls only when necessary and caches the result when onboarded
 * @param {object} provider - The USPS provider to check
 * @returns {{isOnboarded: boolean, isLoading: boolean}} The onboard status and loading state
 */
export const useUspsNsaOnboardStatus = (provider) => {
  const dispatch = useDispatch();
  const isOnboardingCompleted = useUspsNsaCopOnboardingCompleted();
  const [apiState, setApiState] = useAsyncSafeState({ isOnboarded: false, isLoading: true });

  useEffect(() => {
    // If we already know they're onboarded (cached in user setting), no need to check
    if (isOnboardingCompleted) {
      setApiState({ isOnboarded: true, isLoading: false });
      return;
    }

    // If no provider, can't be onboarded
    if (!provider) {
      setApiState({ isOnboarded: false, isLoading: false });
      return;
    }

    // Make API call to check current status
    const checkStatus = async () => {
      try {
        let result = await getUspsOnboardStatus(provider.sellerLabelProviderId);

        // If result is a string, parse it as JSON
        if (typeof result === 'string') {
          result = JSON.parse(result);
        }

        const isOnboarded = result?.IsOnboarded ?? false;

        if (isOnboarded) {
          // Cache the result in user setting so we don't check again
          dispatch(
            saveUserSetting({
              settingName: UserSettings.uspsNsaCopOnboardingCompleted?.settingName,
              settingValue: true,
              excludeV3prefix: true,
            }),
          );
        }

        setApiState({ isOnboarded, isLoading: false });
      } catch {
        setApiState({ isOnboarded: false, isLoading: false }); // Fail gracefully, assume not onboarded
      }
    };

    checkStatus();
  }, [provider, isOnboardingCompleted, dispatch, setApiState]);

  return apiState;
};

/**
 * Custom hook to determine if USPS NSA waiting for approval banner should show
 * Shows when user is onboarded but waiting for USPS approval
 * @returns {boolean} Whether USPS NSA waiting banner should show
 */
export const useShouldShowUspsNsaWaitingBanner = () => {
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const isFeatureEnabled = useIsUspsCopFeatureEnabled();
  const isMerchantEligible = useIsMerchantUspsCopEligible();
  const isOnboardingCompleted = useUspsNsaCopOnboardingCompleted();
  const isBannerDismissed = useUspsNsaWaitingBannerDismissed();

  const firstUspsProvider = useMemo(() => {
    const uspsProviders = sellerLabelProviders.filter((provider) => provider?.labelProviderId === Providers.Stamps.labelProviderId);
    return uspsProviders?.[0] ?? null;
  }, [sellerLabelProviders]);

  // Check API to get current onboard status
  const shouldCheckApi = isFeatureEnabled && isMerchantEligible && firstUspsProvider && !firstUspsProvider.useHouseCredentials;
  const onboardStatus = useUspsNsaOnboardStatus(shouldCheckApi ? firstUspsProvider : null);

  return useMemo(() => {
    // Don't show if user has dismissed the banner
    if (isBannerDismissed) {
      return false;
    }

    // Only show if feature is enabled AND merchant is eligible
    if (!isFeatureEnabled || !isMerchantEligible) {
      return false;
    }

    // No USPS providers
    if (!firstUspsProvider) {
      return false;
    }

    // Using house credentials - no waiting needed
    if (firstUspsProvider.useHouseCredentials) {
      return false;
    }

    // If we have cached onboarding status, show immediately (don't wait for API)
    if (isOnboardingCompleted) {
      return true;
    }

    // Don't show while loading API status (only if we don't have cached status)
    if (shouldCheckApi && onboardStatus.isLoading) {
      return false;
    }

    // Show waiting banner when API confirms they ARE onboarded (waiting for USPS approval)
    if (onboardStatus.isOnboarded) {
      return true;
    }

    return false;
  }, [
    isBannerDismissed,
    isFeatureEnabled,
    isMerchantEligible,
    isOnboardingCompleted,
    firstUspsProvider,
    onboardStatus.isOnboarded,
    onboardStatus.isLoading,
    shouldCheckApi,
  ]);
};

/**
 * Custom hook to determine if the USPS NSA modal was already dismissed during the current login session.
 * Compares the stored dismissal timestamp against currentUser.lastLoginDate.
 * @returns {boolean} Whether the modal is dismissed for this session
 */
export const useIsUspsNsaModalDismissedForSession = () => {
  const dismissedAtUTC = useUspsNsaModalDismissedAtUTC();
  const currentUser = useSelector(Global.selectUserCurrentUserInfo);
  const lastLoginDate = currentUser?.lastLoginDate;

  return useMemo(() => {
    if (!dismissedAtUTC || !lastLoginDate) {
      return false;
    }

    const dismissedDateTime = DateTime.fromISO(dismissedAtUTC, { zone: 'utc' });
    const loginDateTime = DateTime.fromISO(lastLoginDate, { zone: 'utc' });

    if (!dismissedDateTime.isValid || !loginDateTime.isValid) {
      return false;
    }

    // Modal was dismissed after (or at) the most recent login — treat as dismissed for this session
    return dismissedDateTime >= loginDateTime;
  }, [dismissedAtUTC, lastLoginDate]);
};

/**
 * Returns a callback that persists the current UTC timestamp as the modal dismissal time.
 * Co-located with useIsUspsNsaModalDismissedForSession so read/write logic stays together.
 * @returns {() => void} Callback to dismiss the USPS NSA modal for this session
 */
export const useDismissUspsNsaModal = () => {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(
      saveUserSetting({
        settingName: UserSettings.uspsNsaModalDismissedAtUTC.settingName,
        settingValue: getCurrentDateTimeAsUtcIso(),
        excludeV3prefix: true,
      }),
    );
  }, [dispatch]);
};

/**
 * Custom hook to determine if USPS NSA banner should show
 * Uses clean synchronous logic with async hook for API calls
 * @returns {boolean} Whether USPS NSA banner should show
 */
export const useShouldShowUspsNsaBanner = () => {
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const isFeatureEnabled = useIsUspsCopFeatureEnabled();
  const isMerchantEligible = useIsMerchantUspsCopEligible();
  const isOnboardingCompleted = useUspsNsaCopOnboardingCompleted();

  const firstUspsProvider = useMemo(() => {
    const uspsProviders = sellerLabelProviders.filter((provider) => provider?.labelProviderId === Providers.Stamps.labelProviderId);
    return uspsProviders?.[0] ?? null;
  }, [sellerLabelProviders]);

  // Determine if we should even check the API
  const shouldCheckApi =
    isFeatureEnabled && isMerchantEligible && !isOnboardingCompleted && firstUspsProvider && !firstUspsProvider.useHouseCredentials;

  // Only check onboard status if all conditions are met
  const onboardStatus = useUspsNsaOnboardStatus(shouldCheckApi ? firstUspsProvider : null);

  return useMemo(() => {
    // If already onboarded (cached), never show banner
    if (isOnboardingCompleted) {
      return false;
    }

    // Only proceed if feature is enabled AND merchant is eligible
    if (!isFeatureEnabled || !isMerchantEligible) {
      return false;
    }

    // No USPS providers
    if (!firstUspsProvider) {
      return false;
    }

    // Using house credentials
    if (firstUspsProvider.useHouseCredentials) {
      return false;
    }

    // If we need to check the API, DON'T show banner until loading completes
    // This prevents the banner from flashing during initial page load
    if (shouldCheckApi && onboardStatus.isLoading) {
      return false;
    }

    // API confirmed they ARE onboarded
    if (onboardStatus.isOnboarded) {
      return false;
    }

    // ONLY show banner if ALL checks passed AND API confirmed they are NOT onboarded
    return true;
  }, [
    isFeatureEnabled,
    isMerchantEligible,
    isOnboardingCompleted,
    firstUspsProvider,
    onboardStatus.isOnboarded,
    onboardStatus.isLoading,
    shouldCheckApi,
  ]);
};

/**
 * Custom hook to fetch USPS COP instructions link
 * Extracts the common logic used by both banner and modal components
 * Tests each USPS provider to find the first one that returns a valid URL
 * @returns {string|undefined} The instructions link URL or undefined if not available
 */
export const useUspsCopInstructionsLink = () => {
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const [instructionsLink, setInstructionsLink] = useAsyncSafeState(undefined);
  const uspsProviders = useMemo(
    () =>
      sellerLabelProviders.filter(
        (provider) => provider?.labelProviderId === Providers.Stamps.labelProviderId && provider?.requiresSetup === false,
      ),
    [sellerLabelProviders],
  );

  useEffect(() => {
    if (!uspsProviders?.length) {
      return;
    }

    const testProviders = () => {
      const providerPromises = uspsProviders.map((provider) =>
        getSellerProviderAccountLink(provider.sellerLabelProviderId, {
          urlType: 'CarrierManagement',
          applicationContext: 'noheader',
        })
          .then(({ sellerProviderAccountUrl }) => ({
            success: true,
            url: sellerProviderAccountUrl,
            providerId: provider.sellerLabelProviderId,
          }))
          .catch((error) => ({
            success: false,
            error,
            providerId: provider.sellerLabelProviderId,
          })),
      );

      // Test all providers and use the first successful one
      Promise.allSettled(providerPromises)
        .then((results) => {
          const successfulResult = results.map((result) => result.value).find((result) => result?.success && result?.url);

          if (successfulResult) {
            setInstructionsLink(successfulResult.url);
          } else {
            setInstructionsLink(undefined);
          }
        })
        .catch(() => {
          setInstructionsLink(undefined);
        });
    };

    testProviders();
  }, [uspsProviders, setInstructionsLink]);

  return instructionsLink;
};

export const useShouldShowHeaderNotificationBanner = () => {
  const shouldShowTrialWarningBanner = useShouldShowTrialWarningBanner();
  const shouldShowTrialInfoBanner = useShouldShowTrialInfoBanner();
  const shouldShowPastDueBanner = useShouldShowPastDueBanner();
  const isAccountEmailVerified = useIsAccountEmailVerifiedSellerSetting();
  const shouldShowUndoCancellationBanner = useShouldShowUndoCancellationBanner();
  const shouldShowFraudWarning = useIsAccountLimited();
  const showTaxIdBanner = useTaxIdBanner();
  const shouldShowUspsNsaBanner = useShouldShowUspsNsaBanner();
  const shouldShowPhoneVerificationBanner = useShouldShowPhoneVerificationBanner();
  const shouldShowApiKeysExpiringBanner = useShouldShowApiKeysExpiringBanner();
  const shouldShowUspsNsaWaitingBanner = useShouldShowUspsNsaWaitingBanner();
  const { showBanner: shouldShowShippingLimitBanner } = useShippingPlanInfo();
  const shouldShowPlanTrialBanner = useShouldShowPlanTrialBanner();
  const shouldShowPlanTrialExpiredBanner = useShouldShowPlanTrialExpiredBanner();

  const shouldShowPhoneNumberCaptureBanner = useShouldShowPhoneNumberCaptureBanner();

  return (
    shouldShowTrialWarningBanner ||
    shouldShowTrialInfoBanner ||
    shouldShowPlanTrialBanner ||
    shouldShowPlanTrialExpiredBanner ||
    shouldShowPastDueBanner ||
    !isAccountEmailVerified ||
    shouldShowUndoCancellationBanner ||
    shouldShowFraudWarning ||
    showTaxIdBanner ||
    shouldShowUspsNsaBanner ||
    shouldShowPhoneVerificationBanner ||
    shouldShowApiKeysExpiringBanner ||
    shouldShowUspsNsaWaitingBanner ||
    shouldShowShippingLimitBanner ||
    shouldShowPhoneNumberCaptureBanner
  );
};

export const useShouldShowPhoneNumberCaptureBanner = () => {
  const isFeatureEnabled = useEnablePhoneNumberCapture();
  const hasAdminRole = useUserHasAdministrationRole();
  const seller = useSelector(Global.selectSellerData);
  const hasDismissedBanner = usePhoneNumberCaptureBannerDismissed();

  if (!isFeatureEnabled || !hasAdminRole) {
    return false;
  }

  const hasPhoneNumber = Boolean(seller?.phone);
  if (hasPhoneNumber) {
    return false;
  }

  return !hasDismissedBanner;
};
